import React, { useMemo } from "react";
import { Card } from "primereact/card";
import { Chart } from "primereact/chart";
import { CHART_AC, CHART_DC, CHART_NEUTRAL } from "../../../../styles/theme_constants";
import { comparisonLabel } from "../../../../utils/fleetPeriods";
import { change, formatNumber, formatPercent, freeShare, perDay } from "../../../../utils/fleetDerivations";
import { axisLabel, hasUnclassified, splitDrawable } from "./panelHelpers";
import type { PanelProps } from "./panelHelpers";
import { barSeries, baseChartOptions, CHART_HEIGHT } from "./dashboardChart";
import StatTile from "./StatTile";
import UnavailableNotice from "./UnavailableNotice";

/**
 * BOF-76 — charging sessions, the free ones among them, and the AC / DC split where known
 * (FR-016 – FR-019).
 *
 * Counts may be fractional under the 30-day lens (they are scaled), so they are formatted to two places
 * there and whole otherwise.
 */
const SessionsPanel: React.FC<PanelProps> = ({ selected, previous, trend }) => {
  const s = selected.sessions;
  const digits = selected.adjusted ? 2 : 0;
  const split = splitDrawable(trend);
  const showUnclassified = split && hasUnclassified(trend, (p) => p.sessions.unclassified);

  const data = useMemo(() => {
    const labels = trend.map(axisLabel);
    if (!split) {
      return { labels, datasets: [barSeries("Sessions", trend.map((p) => p.sessions.total), CHART_NEUTRAL, true)] };
    }
    return {
      labels,
      datasets: [
        barSeries("AC", trend.map((p) => p.sessions.ac), CHART_AC, false),
        barSeries("DC", trend.map((p) => p.sessions.dc), CHART_DC, !showUnclassified),
        ...(showUnclassified
          ? [barSeries("Unclassified", trend.map((p) => p.sessions.unclassified), CHART_NEUTRAL, true)]
          : []),
      ],
    };
  }, [trend, split, showUnclassified]);

  return (
    <Card title="Charging sessions" className="h-full">
      <div className="grid mb-2">
        <div className="col-12 sm:col-4">
          <StatTile
            label="Sessions"
            value={formatNumber(s.total, digits)}
            change={change(s.total, previous?.sessions.total)}
            comparedTo={comparisonLabel(previous)}
          />
        </div>
        <div className="col-6 sm:col-4">
          <StatTile label="Per day" value={formatNumber(perDay(s.total, selected.daysElapsed), 1)} />
        </div>
        <div className="col-6 sm:col-4">
          <StatTile
            label="Free sessions"
            value={`${formatNumber(s.free, digits)} (${formatPercent(freeShare(s.free, s.total))})`}
            hint="Sessions that carried no charge, and their share of all sessions."
          />
        </div>
      </div>
      {split ? (
        <div className="text-xs text-color-secondary mb-2">
          AC {formatNumber(s.ac, digits)} · DC {formatNumber(s.dc, digits)}
          {s.unclassified > 0 && <> · Unclassified {formatNumber(s.unclassified, digits)}</>}
        </div>
      ) : (
        <div className="mb-2">
          <UnavailableNotice reason={{ kind: "splitUnavailable" }} inline />
        </div>
      )}
      <Chart type="bar" data={data} options={baseChartOptions({ stacked: split, yTitle: "Sessions" })} style={CHART_HEIGHT} />
    </Card>
  );
};

export default SessionsPanel;
